import { ReactNode, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./style.module.scss";
import PrintPage, { EXPORT_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintPageFooter from "@components/print/PrintElements/PrintPageFooter";
import { useAppSelector } from "@/store/hooks";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import DocumentInfo from "@components/print/PrintElements/DocumentInfo";
import ProductList from "@components/print/PrintElements/ProductList";
import { BuyInvoiceApi, BuyInvoiceDataType } from "@/api/BuyInvoiceApi";
import { BuyInvoiceFormDataKeys } from "@/generated/BuyInvoiceFormDataKeys";
import { EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { formatDate } from "@utils/Api";
import { CustomerInfoBase } from "@/formTypes/DocumentClientType";
import { chunkArray } from "@utils/Utils";
import { sortProductsByOrder } from "@utils/EDocument";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";
import { buyInvoiceRelatedAttributes } from "@/formTypes/BuyInvoiceDataType";
import { ProviderApi } from "@/api/ProviderApi";

const BuyInvoicePrint = () => {
  const params = useParams()
  const { id: buyInvoiceId, autoPrint = false, copies = 0 } = params;

  const suscription = useAppSelector((state) => { return state.suscription.suscription });
  const maxItemPerPage = Number(suscription?.items_per_page || 30)
  const [buyInvoice, setBuyInvoice] = useState<BuyInvoiceDataType>({})
  const [buyInvoiceProdCount, setBuyInvoiceProdCount] = useState<number>(0)
  const [buyInvoicesProducts, setBuyInvoicesProducts] = useState<EDocumentProductDataType[]>()
  const [documentPages, setDocumentPages] = useState<EDocumentProductDataType[][]>()
  const [searchParams] = useSearchParams();
  const exportFlag = searchParams.get(EXPORT_PDF_QUERY_PARAM);

  useEffect(() => {
    import("@printerVariation/generic.scss");
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadBuyInvoiceData()
  }, []);

  useEffect(() => {
    const pages = chunkArray(buyInvoicesProducts || [], maxItemPerPage)
    setDocumentPages(pages)
  }, [maxItemPerPage, buyInvoicesProducts]);

  const loadBuyInvoiceData = async () => {
    if (buyInvoiceId) {
      let buyInvoice = await BuyInvoiceApi.get(buyInvoiceId, {
        selectionSet: [
          ...BuyInvoiceFormDataKeys.filter(k => !buyInvoiceRelatedAttributes.includes(k)),
          "currency.*",
          "buyInvoiceProducts.*",
        ]
      })
      const provider = await ProviderApi.get(buyInvoice.providerId || "");
      buyInvoice = {
        ...buyInvoice,
        provider
      }
      setBuyInvoicesProducts(sortProductsByOrder(buyInvoice.buyInvoiceProducts))
      setBuyInvoiceProdCount(buyInvoice.buyInvoiceProducts?.length || 0)
      setBuyInvoice(buyInvoice)
    }
    PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
  }

  function toCustomerInfoBase(p: BuyInvoiceDataType): CustomerInfoBase {
    if (!p?.nombre_factura) throw new Error("nombre_factura requerido");
    return {
      nombre_factura: p.nombre_factura,
      razon_social: p.razon_social ?? "",
      tel1: p.tel1 ?? "",
      tel2: p.tel2 ?? "",
      email: p.email ?? "",
      direccion: p.direccion ?? "",
      clientActivitycode: p.clientActivitycode ?? "",
      plazo: p.plazo ?? null,
      client: p.client ?? null,
      tipo_identificacion: p.tipo_identificacion ?? "",
      identificacion: p.identificacion ?? "",
    };
  }

  const renderPage = (documentPages: EDocumentProductDataType[][]): ReactNode[] => {
    return documentPages.map((page, index) => {
      return (
        <PrintPage
          nula={buyInvoice.anulada || false}
          autoPrint={autoPrint === "1"}
          copies={Number(copies)}
        >
          <PrintPageHeader suscription={suscription} />
          <div className="page-content">
            <PrintDocumentHeader title={`Factura de Compra #${buyInvoice.numero}`} fecha={formatDate(buyInvoice.fecha, "YYYY-MM-DD", "DD.MM.YY")} />
            <DocumentInfo
              customer={toCustomerInfoBase(buyInvoice)}
              docType="Factura"
              docSequenceData={{
                docKey: buyInvoice.invoice_key || "",
                sequence: buyInvoice.sequence || ""
              }}
            />
            <ProductList
              maxItemPerPage={maxItemPerPage}
              products={page}
              productCount={buyInvoiceProdCount}
            />
            <PrintPageFooter
              currencyCode={buyInvoice.currency.currency_id}
              exchangeRate={buyInvoice.exchange_rate || 0}
              comments={[
                `${buyInvoice.document_comments}`
              ]}
              summary={{
                subtotal: buyInvoice.subtotal || 0,
                descuento: buyInvoice.descuento || 0,
                impuestos: buyInvoice.impuestos || 0,
                total: buyInvoice.total || 0
              }}
              currentPage={(index + 1)}
              totalPages={documentPages.length} />
          </div>

        </PrintPage>
      )
    });
  }

  return (
    <div>
      {buyInvoice?.id && documentPages && renderPage(documentPages)}
      {!buyInvoice.id && (
        <h3> Cargando </h3>
      )}

    </div>
  );
}

export default BuyInvoicePrint;
